import { useEffect, useState } from 'react';

/**
 * iter311 — usePaymentsStatus hook.
 *
 * Subscribes the caller to the canonical `/api/payments/status`
 * endpoint so any payment-related screen can branch on `enabled` and
 * surface the bilingual `reason_en` / `reason_ar` to users.
 *
 * Single source of truth for the dynamic admin kill-switch on the
 * frontend. The hook is intentionally tiny — no caching layer, no
 * polling — payments are operator-toggled, so a fresh fetch per mount
 * is fine. If a screen needs live updates (admin console), it can
 * pass `{ refreshMs }` to enable polling.
 *
 * @param {{ refreshMs?: number }} [opts]
 * @returns {{
 *   enabled: boolean,
 *   reason: string,
 *   reason_en: string,
 *   reason_ar: string,
 *   source: 'env' | 'admin' | null,
 *   since: string | null,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export default function usePaymentsStatus(opts = {}) {
  const { refreshMs = 0 } = opts;
  const [state, setState] = useState({
    enabled: true,
    reason: '',
    reason_en: '',
    reason_ar: '',
    source: null,
    since: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/status`,
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setState({
            enabled: Boolean(data.enabled),
            reason: data.reason || '',
            reason_en: data.reason_en || '',
            reason_ar: data.reason_ar || '',
            source: data.source || null,
            since: data.since || null,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          // Fail-open: if the status endpoint is unreachable, assume
          // payments are enabled. Buttons will hit the 503 path which
          // is also handled. Better than incorrectly blocking the UI
          // because of a transient network blip.
          setState((s) => ({ ...s, loading: false, error: String(err) }));
        }
      }
    };
    fetchStatus();
    let id;
    if (refreshMs > 0) {
      id = setInterval(fetchStatus, refreshMs);
    }
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [refreshMs]);

  return state;
}
